<template>
  <div
    :id="id"
    ref="ad"
    class="ad-slot"
    :class="classes"
    :data-test-id="testId"
  />
</template>

<script>

import kebabCase from '@/utils/kebab-case';
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'AdSlot',
  props: {
    /**
     * The mapping the slot should use. A corresponding mapping must be defined
     * in plugins/gam.js
     */
    mapping: {
      type: String,
      default: '',
    },
    /**
     * This is a string because it is coming from JSON which encodes booleans as
     * strings. Never use this prop directly, always use the dynamicBoolean computed.
     */
    dynamic: {
      type: String,
      default: 'false',
    },
    sticky: {
      type: Boolean,
      default: false,
    },
    /**
     * This prop is unused in this component. It is data for ad sections
     * that is used in SidebarAds.vue.
     */
    displayInSidebar: {
      type: Boolean,
    },
  },
  data() {
    return {
      index: this.$store.state.gam.adsOnPage,
      observer: undefined,
      initialized: false,
      slot: null,
    };
  },
  computed: {
    ...mapState('gam', ['empty_slots']),
    ...mapGetters(['dataLayer']),
    dynamicBoolean() {
      return this.dynamic === 'true';
    },
    id() {
      return `gam_${this.index}`;
    },
    adUnit() {
      return `/${this.$gam.network_id}/${this.dataLayer.ad_prefix}/${this.dataLayer.post_type}`;
    },
    sizeMapping() {
      try {
        return window.googletag.mappedSizes[this.mapping];
      } catch (e) {
        this.$logger.error(`Mappings for ${this.mapping} are not defined`);
        return null;
      }
    },
    adsEnabled() {
      return process.browser && this.$gam.adsEnabled();
    },
    size() {
      return this.sizeMapping ? this.sizeMapping.slice(-1)[0][1] : [];
    },
    classes() {
      const classes = [];
      if (this.mapping) {
        // kebab-case the slot mapping for use as a BEM class
        classes.push(`ad-slot--${kebabCase(this.mapping)}`);
      }
      if (this.dynamicBoolean || !this.mapping) {
        classes.push('ad-slot--dynamic');
      }
      return classes.join(' ');
    },
    testId() {
      const testIdArray = ['ad-slot'];
      if (this.mapping) {
        testIdArray.unshift(kebabCase(this.mapping));
      }
      if (this.dynamicBoolean || !this.mapping) {
        testIdArray.unshift('dynamic');
      }
      return testIdArray.join('-');
    },
  },
  watch: {
    empty_slots(newValue) {
      if (!newValue.length && this.observer) {
        this.observer.disconnect();
      }
    },
  },
  created() {
    if (!this.adsEnabled) { return; }
    this.$store.dispatch('gam/incrementAdsOnPage');
  },
  mounted() {
    this.init();
    this.initialized = true;
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.adsEnabled && this.slot) {
      window.googletag.cmd.push(() => {
        window.googletag.destroySlots([this.slot]);
      });
      this.$store.dispatch('gam/removeFromEmptySlots', this.slot);
    }
  },
  methods: {
    init() {
      if (!this.adsEnabled) { return; }
      window.googletag.cmd.push(() => {
        this.define();
        this.lazyLoad();
      });
      if (this.sticky) {
        // eslint-disable-next-line global-require
        const stickyFill = require('stickyfilljs');
        stickyFill.add(this.$refs.ad);
      }
    },
    define() {
      const slot = window.googletag.defineSlot(this.adUnit, this.size, this.id);
      slot
        .defineSizeMapping(this.sizeMapping)
        .addService(window.googletag.pubads())
        .setTargeting('slot', this.id);

      this.slot = slot;

      this.$store.dispatch('gam/pushEmptySlots', slot);
      window.googletag.display(this.id);
    },
    lazyLoad() {
      this.observer = new IntersectionObserver((slots) => {
        slots.forEach((slot) => {
          if (!slot.isIntersecting) {
            return;
          }
          this.observer.unobserve(slot.target);

          this.display();
        });
      }, { rootMargin: '150px' });
      this.observer.observe(this.$refs.ad);
    },
    display() {
      // Do not use this in cmd.push as the reference can change when the callback runs
      const slotToRefresh = this.slot;
      if (!slotToRefresh) return;

      window.googletag.cmd.push(() => {
        this.$store.dispatch('gam/removeFromEmptySlots', slotToRefresh);
        window.googletag.pubads().refresh([slotToRefresh], { changeCorrelator: false });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_ad-slot';
</style>

<style lang="scss"> // no scoped - to reach inner div

.ad-slot--leaderboard > div {
  display: inline-block;
}

// NEXUS-6858 make sure bg is white for text
.ad-slot[data-google-query-id] > div > iframe {
  background: white;
}
</style>

<docs>
This component is a placeholder for ads to be injected.

*AdSlot components must avoid doing anything besides rendering the placeholder markup
on the server side since most of this component's functionality depends on
window.googletag which is not available server-side.*
</docs>
